import React from 'react';
import {
    Box,
    Button,
    Collapse,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';

type PatientSearchResultsProps = {
    patients: DICOMPatientPresentation[];
    expandedPatients?: Record<string, boolean>;
    onToggleExpanded?: (id: string) => void;
    onImportImages: (patient: DICOMPatientPresentation) => void;
    collapsible?: boolean;
};

const PatientSearchResults = React.memo(
    ({
        patients,
        expandedPatients,
        onToggleExpanded,
        onImportImages,
        collapsible,
    }: PatientSearchResultsProps) => {
        const intl = useIntl();
        return (
            <TableContainer
                component={Paper}
                sx={{
                    height: '100%',
                    overflow: 'auto',
                    overscrollBehavior: 'contain',
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {collapsible && <TableCell width={40}></TableCell>}
                            <TableCell>
                                <FormattedMessage id="name" />
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id="id" />
                            </TableCell>
                            <TableCell align="right">
                                <FormattedMessage id="actions" />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {patients.map((p, index) => {
                            const expanded =
                                collapsible &&
                                !!expandedPatients?.[p.patientID || ''];
                            return (
                                <React.Fragment key={p.patientID || index}>
                                    <TableRow hover>
                                        {collapsible && (
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onToggleExpanded?.(
                                                            p.patientID || ''
                                                        )
                                                    }
                                                    aria-label={intl.formatMessage(
                                                        {
                                                            id: expanded
                                                                ? 'collapse'
                                                                : 'expand',
                                                        }
                                                    )}
                                                >
                                                    {expanded ? (
                                                        <ExpandLess />
                                                    ) : (
                                                        <ExpandMore />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <SensitiveInfoTypography>
                                                {p.patientName || (
                                                    <FormattedMessage id="unknown" />
                                                )}
                                            </SensitiveInfoTypography>
                                        </TableCell>
                                        <TableCell>
                                            <SensitiveInfoTypography>
                                                {p.patientID}
                                            </SensitiveInfoTypography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                    onImportImages(p)
                                                }
                                            >
                                                <FormattedMessage id="selectPatient" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {collapsible && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                sx={{ p: 0, border: 0 }}
                                            >
                                                <Collapse
                                                    in={expanded}
                                                    timeout="auto"
                                                    unmountOnExit
                                                >
                                                    <Box px={2} py={1}>
                                                        {p.studies?.map(
                                                            (study) => (
                                                                <Box
                                                                    key={
                                                                        study.studyInstanceUID
                                                                    }
                                                                    mb={1}
                                                                >
                                                                    <Typography variant="subtitle2">
                                                                        <FormattedMessage id="study" />{' '}
                                                                        {
                                                                            study.studyInstanceUID
                                                                        }
                                                                    </Typography>
                                                                    <Divider
                                                                        sx={{
                                                                            my: 1,
                                                                        }}
                                                                    />
                                                                    {study.series &&
                                                                        study.series.map(
                                                                            (
                                                                                ser
                                                                            ) => (
                                                                                <Box
                                                                                    key={
                                                                                        ser.seriesInstanceUID
                                                                                    }
                                                                                    display="grid"
                                                                                    gridTemplateColumns="2fr 1fr 1fr 1fr auto"
                                                                                    alignItems="center"
                                                                                    py={
                                                                                        0.5
                                                                                    }
                                                                                    gap={
                                                                                        2
                                                                                    }
                                                                                >
                                                                                    <Typography variant="body2">
                                                                                        <SensitiveInfoTypography>
                                                                                            {ser.seriesDescription || (
                                                                                                <FormattedMessage id="unnamedSeries" />
                                                                                            )}
                                                                                        </SensitiveInfoTypography>
                                                                                    </Typography>
                                                                                    <Typography variant="body2">
                                                                                        {ser.modality ||
                                                                                            '-'}
                                                                                    </Typography>
                                                                                    <Typography variant="body2">
                                                                                        <SensitiveInfoTypography>
                                                                                            {ser.seriesDate ||
                                                                                                '-'}
                                                                                        </SensitiveInfoTypography>
                                                                                    </Typography>
                                                                                    <Typography variant="body2">
                                                                                        {ser.seriesInstanceUID.substring(
                                                                                            0,
                                                                                            15
                                                                                        )}
                                                                                        ...
                                                                                    </Typography>
                                                                                </Box>
                                                                            )
                                                                        )}
                                                                </Box>
                                                            )
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }
);
PatientSearchResults.displayName = 'PatientSearchResults';

export default PatientSearchResults;
